import type { AxiosResponse } from 'axios'

export namespace Models {
  export interface VehicleField {
    field: string
    segment: string
    source: string
    customizeSourceField: string
    fallbackSource: string
    fallbackSourceField: string
    other?: any
    [key: string]: any
  }

  export interface ExceptionRule {
    delete: any
    source: InventorySources.SourceType
    condition: InventorySources.ConditionType
    operator: InventorySources.OperatorType
    value: string
    action: InventorySources.ActionType
    logicalOperator: InventorySources.LogicalOperatorType
  }

  export interface Segment {
    id: string
    name: string
    feed: InventorySources.SegmentFeedType
    criteria: SegmentCriteria[]
    action: any
    filter: {
      [key: string]: string
    }
  }

  export interface SegmentCriteria {
    field: string
    operator: InventorySources.OperatorType
    value: string
    logicalOperator: InventorySources.LogicalOperatorType
  }

  export interface Vehicle {
    selected: boolean
    vin: string
    stock: string
    status: string
    sources: {
      homenet: boolean
      fdds: boolean
      vauto: boolean
    }
    listed: boolean
  }

  export interface FieldFeedTemplate {
    feed: string
    field: string
    display_name: string
    description: string
    example: string
  }
  export interface FieldFeedConfig {
    feed: string
    field: string
    fallback?: Array<{
      feed: string
      field: string
    }>
  }

  export interface Field {
    field: string
    source: InventorySources.SourceType
    sourceField: string
  }

  export interface CustomField {
    field: string
    custom: true
    label: string
    type: 'string' | 'number' | 'boolean' | 'date'
    sources: Array<{
      id: string
      field: string
      path: string | null
    }>
    required: boolean
    policy: {
      on_missing: 'fallback' | 'skip' | 'error'
    }
    default: any
    indexing_hint: {
      searchable: boolean
      facetable: boolean
      sortable: boolean
    }
  }

  export interface FieldMapping {
    field: string
    sources: string[]
    required: boolean
    policy: {
      on_missing: string
      default_value?: string
    }
  }

  export interface CustomFieldMapping extends Omit<FieldMapping, 'sources'> {
    custom: true
    label: string
    type: string
    sources: Array<{
      id: string
      field: string
      path: string | null
    }>
    default: any
    indexing_hint: {
      searchable: boolean
      facetable: boolean
      sortable: boolean
    }
  }
}

export namespace UI {
  export interface TableHeader {
    width?: string
    title: string
    key: string
    sortable?: boolean
    align?: 'start' | 'end' | 'center' | undefined
    children?: Array<TableHeader>
  }

  export interface CarouselImage {
    url: string
    title?: string
    subtitle?: string
  }
}

export namespace InventorySources {
  export const SOURCES = ['homenet', 'chrome', 'fdds_v3'] as const
  export type SourceType = (typeof SOURCES)[number]

  export const SOURCE_FIELDS = [
    { label: 'Vehicle Year', value: 'Year' },
    { label: 'Vehicle Year', value: 'year' },
    { label: 'Vehicle Year', value: 'Model.Year' },
    { label: 'Vehicle Make', value: 'Make' },
    { label: 'Vehicle Make', value: 'make' },
    { label: 'Vehicle Make', value: 'Model.Make' },
    { label: 'Vehicle Model', value: 'Model' },
    { label: 'Vehicle Model', value: 'model' },
    { label: 'Vehicle Model', value: 'Model.ModelName' },
    { label: 'Vehicle Trim', value: 'vehicles[0].trim' },
    { label: 'Vehicle Trim', value: 'Model.Trim' },
    { label: 'Exterior Color Name', value: 'ExteriorColor' },
    { label: 'Generic Color Name', value: 'exteriorColors[0].genericDesc' },
    { label: 'Manufacturer Specific Color Name', value: 'exteriorColors[0].description' },
    { label: 'Exterior Color Name', value: 'ExtColorName' },
    { label: 'Interior Color Name', value: 'InteriorColor' },
    { label: 'Generic Interior Color', value: 'interiorColors[0].genericDesc' },
    { label: 'Detailed Interior Color', value: 'interiorColors[0].description' },
    { label: 'Interior Color Name', value: 'IntColorName' },
    { label: 'Body Style', value: 'vehicles[0].bodyType' },
    { label: 'Body Style', value: 'NormalizedBodyStyle' },
    { label: 'Body Style', value: 'BodyStyle' },
    { label: 'Drive Type', value: 'vehicles[0].driveType' },
    { label: 'Drive Type', value: 'NormalizeDrive' },
    { label: 'Drive Type', value: 'DriveTrain' },
    { label: 'Vehicle Mileage', value: 'Odometer' },
    { label: 'Vehicle Mileage', value: 'DLRMileage' },
    { label: 'Selling Price', value: 'SellingPrice' },
    { label: 'Dealer Price', value: 'Pricing.DealerPrice' },
    { label: 'Stock Number', value: 'Stock' },
    { label: 'Stock Number', value: 'StockNum' },
    { label: 'Transmission Type', value: 'customField:TRANSMISSION_TYPE' },
    { label: 'Transmission Type', value: 'NormalizeTransmission' },
    { label: 'Transmission Type', value: 'Transmission' },
    { label: 'Engine Cylinders', value: 'customField:ENGINE_CYLINDERS' },
    { label: 'Engine Type', value: 'NormalizeEngine' },
    { label: 'Engine Cylinders', value: 'Cylinders' },
    { label: 'City MPG', value: 'MPG.City' },
    { label: 'City MPG', value: 'fuelEconomy.city' },
    { label: 'Highway MPG', value: 'MPG.Highway' },
    { label: 'Highway MPG', value: 'fuelEconomy.highway' },
    { label: 'Vehicle Condition', value: 'Type' },
    { label: 'Vehicle Condition', value: 'NewUsed' },
    { label: 'Certified Pre-Owned', value: 'Certified' },
    { label: 'Certified Pre-Owned', value: 'CPO' },
    { label: 'Vehicle Features', value: 'customField:FEATURES_ARRAY' },
    { label: 'Vehicle Options', value: 'Options' },
    { label: 'Seating Capacity', value: 'Seating' },
    { label: 'Passenger Capacity', value: 'vehicles[0].passengerCapacity' },
  ] as const

  export type SourceField = (typeof SOURCE_FIELDS)[number]['value']

  export const SEGMENTATION_OPTIONS = ['new', 'used', 'certified'] as const
  export type SegmentationType = (typeof SEGMENTATION_OPTIONS)[number]

  export const CONDITION_OPTIONS = ['status', 'VIN', 'stock', 'year', 'make', 'model'] as const
  export type ConditionType = (typeof CONDITION_OPTIONS)[number]

  export const LOGICAL_OPERATORS = ['AND', 'OR'] as const
  export type LogicalOperatorType = (typeof LOGICAL_OPERATORS)[number]

  export const OPERATOR_OPTIONS = [
    'equals',
    'contains',
    'starts with',
    'ends with',
    'not equal to',
  ] as const
  export type OperatorType = (typeof OPERATOR_OPTIONS)[number]

  export const ACTION_OPTIONS = ['List', 'Exclude'] as const
  export type ActionType = (typeof ACTION_OPTIONS)[number]

  export const SEGMENT_FEEDS = ['HomeNet', 'VAuto', 'FDDS', 'Chrome'] as const
  export type SegmentFeedType = (typeof SEGMENT_FEEDS)[number]
}

export namespace State {
  export interface VehicleStore {
    mappingType: string
    segmentation: InventorySources.SegmentationType
    fields: Models.VehicleField[]
    fieldHeaders: UI.TableHeader[]

    primarySource: InventorySources.SourceType
    fallbackBehavior: string
    viewMode: string
    search: string
    exceptionRules: Models.ExceptionRule[]
    ruleHeaders: UI.TableHeader[]
    vehicles: Models.Vehicle[]
    vehicleHeaders: UI.TableHeader[]
    filteredVehicles: Models.Vehicle[]

    moveFieldUp: (index: number) => void
    moveFieldDown: (index: number) => void
    addNewField: () => void
    addExceptionRule: () => void
    removeRule: (index: number) => void
    getStatusColor: (status: string) => string
  }
}

export namespace IApp {
  export enum ILoadingStatus {
    Idle = 'idle',
    Loading = 'loading',
    Success = 'success',
    Error = 'error',
  }
  export type IApiResponse<T> =
    | {
        status: true
        data: T
        headers: AxiosResponse['headers']
        message?: string
        title?: string
        type?: string
      }
    | {
        status: false
        message: string
        data?: T
        title: string
        type: string
      }
}
